/**
 * DAC customization: level product card prices within each visual row.
 *
 * Measures `.product-card__copy` (title/subtitle block) per row and sets a matching
 * min-height so `.product-card__price-row` starts on the same line across cards.
 *
 * Tab switches: level synchronously when a panel is shown (before paint / while the
 * enter fade is still at opacity 0) so heights do not visibly jump after the fact.
 */

const COPY_SELECTOR = ".product-card__copy";
const CARD_SELECTOR = ".product-card";
const ROW_TOLERANCE_PX = 2;

/**
 * @param {() => void} fn
 * @param {number} wait
 * @returns {() => void}
 */
function debounce(fn, wait) {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timer;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(fn, wait);
  };
}

/**
 * @param {HTMLElement} card
 * @returns {boolean}
 */
function isCardVisible(card) {
  // Skip cards in inactive / lazy tab panels and other hidden ancestors
  if (card.closest("[hidden]")) return false;

  if (typeof card.checkVisibility === "function") {
    return card.checkVisibility({ checkOpacity: false, checkVisibilityCSS: true });
  }

  return card.getClientRects().length > 0;
}

/**
 * @param {ParentNode} [root]
 * @returns {HTMLElement[]}
 */
function getCards(root = document) {
  return Array.from(root.querySelectorAll(CARD_SELECTOR)).filter((card) => {
    if (!(card instanceof HTMLElement)) return false;
    // Skip carousel clones / off-flow slides where row math is unreliable
    if (card.closest(".swiper-slide-duplicate")) return false;
    if (!isCardVisible(card)) return false;
    return Boolean(card.querySelector(COPY_SELECTOR) && card.querySelector(".product-card__price-row"));
  });
}

/**
 * @param {HTMLElement[]} cards
 */
function resetCopyHeights(cards) {
  for (const card of cards) {
    const copy = card.querySelector(COPY_SELECTOR);
    if (copy instanceof HTMLElement) {
      copy.style.minHeight = "";
    }
  }
}

/**
 * Group cards that share the same visual row (similar viewport top).
 * @param {HTMLElement[]} cards
 * @returns {HTMLElement[][]}
 */
function groupCardsByRow(cards) {
  /** @type {{ top: number, cards: HTMLElement[] }[]} */
  const rows = [];

  for (const card of cards) {
    const top = card.getBoundingClientRect().top;
    const row = rows.find((entry) => Math.abs(entry.top - top) <= ROW_TOLERANCE_PX);
    if (row) {
      row.cards.push(card);
    } else {
      rows.push({ top, cards: [card] });
    }
  }

  return rows.map((entry) => entry.cards);
}

/**
 * @param {ParentNode} [root]
 */
export function levelProductCardPrices(root = document) {
  const cards = getCards(root);
  if (cards.length === 0) return;

  resetCopyHeights(cards);

  // Read after reset so natural heights are accurate
  const rows = groupCardsByRow(cards);

  for (const row of rows) {
    if (row.length < 2) continue;

    let maxHeight = 0;
    for (const card of row) {
      const copy = card.querySelector(COPY_SELECTOR);
      if (!(copy instanceof HTMLElement)) continue;
      maxHeight = Math.max(maxHeight, copy.getBoundingClientRect().height);
    }

    if (maxHeight <= 0) continue;

    const minHeight = `${Math.ceil(maxHeight)}px`;
    for (const card of row) {
      const copy = card.querySelector(COPY_SELECTOR);
      if (copy instanceof HTMLElement) {
        copy.style.minHeight = minHeight;
      }
    }
  }
}

/**
 * Immediate level (no debounce) — used for tab shows so layout is correct before paint.
 * @param {ParentNode} [root]
 */
function flushLevel(root = document) {
  levelProductCardPrices(root);
}

const scheduleLevel = debounce(() => {
  requestAnimationFrame(() => {
    levelProductCardPrices();
  });
}, 100);

/**
 * Level the active (visible) tab panel right away.
 * Runs in a microtask after TabsComponent finishes selectTab / unhide / animateIn setup,
 * still before the browser paints the faded-in content.
 * @param {Element | null} [fromEl]
 */
function flushActiveTabPanel(fromEl) {
  const tabs =
    (fromEl instanceof Element && fromEl.closest("tabs-component")) ||
    document.querySelector("tabs-component");
  if (!(tabs instanceof Element)) {
    flushLevel();
    return;
  }

  const panel = tabs.querySelector(".tabs__panel:not([hidden])");
  if (panel instanceof HTMLElement) {
    flushLevel(panel);
    return;
  }

  flushLevel();
}

/**
 * @param {Event} event
 */
function onTabsInteraction(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (!target.closest("tabs-component [role='tab']")) return;

  // After TabsComponent selectTab (same event turn), before paint
  queueMicrotask(() => {
    flushActiveTabPanel(target);
  });
}

/**
 * Observe tab panels for lazy content inject and hidden toggles.
 * Flush synchronously when a panel becomes visible so copy heights settle pre-paint.
 */
function observeTabPanelsAndGrids() {
  if (typeof MutationObserver !== "undefined") {
    const panelObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const panel = mutation.target;
        if (!(panel instanceof HTMLElement)) continue;

        if (mutation.type === "attributes" && mutation.attributeName === "hidden") {
          if (!panel.hidden) {
            flushLevel(panel);
          }
          continue;
        }

        if (mutation.type === "childList" && mutation.addedNodes.length > 0 && !panel.hidden) {
          flushLevel(panel);
        }
      }
    });

    for (const panel of document.querySelectorAll("tabs-component .tabs__panel")) {
      panelObserver.observe(panel, {
        attributes: true,
        attributeFilter: ["hidden"],
        childList: true,
      });
    }
  }

  const gridSelector =
    ".product-grid, .featured-collection__inner, .product-recommendations__inner, .main-collection-grid, #ResultsList";

  if (typeof ResizeObserver === "undefined") return;

  const resizeObserver = new ResizeObserver(scheduleLevel);
  /** @type {WeakSet<Element>} */
  const observed = new WeakSet();

  const observeGrids = (root = document) => {
    for (const grid of root.querySelectorAll(gridSelector)) {
      if (observed.has(grid)) continue;
      observed.add(grid);
      resizeObserver.observe(grid);
    }
  };

  observeGrids();

  // Newly injected tab grids (from <template>) need observing too
  if (typeof MutationObserver !== "undefined") {
    const gridMountObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches?.(gridSelector)) {
            observeGrids(node.parentElement || document);
          } else {
            observeGrids(node);
          }
        }
      }
    });

    for (const panels of document.querySelectorAll("tabs-component .tabs__panels")) {
      gridMountObserver.observe(panels, { childList: true, subtree: true });
    }
  }
}

function initPriceLeveling() {
  scheduleLevel();

  window.addEventListener("resize", scheduleLevel, { passive: true });
  document.addEventListener("shopify:section:load", scheduleLevel);
  // Collection filters / results morph
  document.addEventListener("filter:update", () => {
    // Wait for ResultsList morph to settle
    requestAnimationFrame(() => {
      setTimeout(scheduleLevel, 50);
    });
  });

  // Product tabs: click / keyboard reveal a (possibly lazy-loaded) panel
  document.addEventListener("click", onTabsInteraction, true);
  document.addEventListener("keydown", onTabsInteraction, true);

  if (document.fonts?.ready) {
    document.fonts.ready.then(scheduleLevel).catch(() => {});
  }

  observeTabPanelsAndGrids();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initPriceLeveling, { once: true });
} else {
  initPriceLeveling();
}
